import type { ListEntity, SavedView, ViewFilter } from '../api/types';

/** MRR > 0 today: the same accounts as the Paying subscriptions KPI at the current month. */
const PAYING: ViewFilter = { field: 'paying', op: 'isTrue' };
/** Stripe billing types (license rows without Stripe billing have billing Trial or Unbilled). */
const STRIPE = ['Yearly', 'Monthly', 'Enterprise contract'];
/** Billed through Stripe. */
const STRIPE_BILLING: ViewFilter = { field: 'billing', op: 'in', value: STRIPE };
/** Subscription states with a running license or Stripe subscription (not ended). */
const LIVE = ['Active', 'Grace'];
/**
 * Users and apps of teams whose main subscription is a Stripe subscription in Active or Grace (the Users
 * tiles' set). State and billing are separate fields: a running trial or unbilled license is Active too.
 */
const ON_PAYING: ViewFilter[] = [
  { field: 'subscriptionLifecycle', op: 'in', value: LIVE },
  { field: 'subscriptionBilling', op: 'in', value: STRIPE },
];

/**
 * Predefined views (UIUX/v1/demodata/views.json) adapted to the real data model:
 * sign-in method and data source size are not recorded, so the views that used them are replaced
 * by views over fields the data does contain. App opens come from the event tables (bi.*), as the
 * 30-day usage columns of the Apps list. The "All …" views cover the whole data: every Open as App
 * license, team member and app, not only Stripe customers.
 */
/** Per list; the first view ("All …") is the default and the base of drill-downs and new views. */
export const PREDEFINED_VIEWS: Record<ListEntity, [SavedView, ...SavedView[]]> = {
  subscriptions: [
    {
      id: 'all',
      name: 'All subscriptions',
      description:
        'Every Open as App license (api.Subscriptions), with its Stripe subscription, plus Stripe subscriptions without a license',
      columns: [
        'name',
        'offer',
        'billing',
        'lifecycle',
        'source',
        'mrr',
        'activeUsers',
        'publishedApps',
        'started',
        'nextDate',
      ],
      filters: [],
      sort: { field: 'mrr', dir: 'desc' },
    },
    {
      id: 'paying',
      name: 'Paying',
      description: 'Subscriptions with MRR today: the Paying subscriptions KPI',
      columns: ['name', 'offer', 'billing', 'addons', 'mrr', 'usage', 'nextDate'],
      filters: [PAYING],
      sort: { field: 'mrr', dir: 'desc' },
    },
    {
      id: 'stripe',
      name: 'Stripe subscriptions',
      description: 'Stripe subscriptions with recurring EUR revenue, paying or ended',
      columns: ['name', 'offer', 'billing', 'lifecycle', 'mrr', 'lastMrr', 'started', 'ended'],
      filters: [STRIPE_BILLING],
      sort: { field: 'mrr', dir: 'desc' },
    },
    {
      id: 'trials',
      name: 'Trials ending soon',
      description: 'Trial licenses running today, earliest end date first',
      columns: ['name', 'domain', 'users', 'apps', 'publishedApps', 'started', 'nextDate'],
      filters: [
        { field: 'billing', op: 'in', value: ['Trial'] },
        { field: 'lifecycle', op: 'in', value: ['Active'] },
      ],
      sort: { field: 'nextDate', dir: 'asc' },
    },
    {
      id: 'renewal-risk',
      name: 'Renewals at risk',
      description:
        'Yearly plans and contracts due in the next 90 days or overdue, with a renewal risk',
      columns: ['name', 'offer', 'billing', 'mrr', 'usage', 'renewalRisk', 'renewalDue'],
      filters: [
        {
          field: 'renewalRisk',
          op: 'in',
          value: [
            'Grace · unpaid',
            'Renewal not billed',
            'Cancels at period end',
            'No active users in 30 days',
            'Low usage',
          ],
        },
        // Overdue unpaid renewals have negative days and count in every horizon, as on Renewals.
        { field: 'renewalInDays', op: 'lte', value: 90 },
      ],
      sort: { field: 'renewalDue', dir: 'asc' },
    },
    {
      id: 'recovery',
      name: 'In recovery',
      description: 'Stripe subscriptions deactivated or disabled. Revenue is still recoverable.',
      columns: ['name', 'offer', 'billing', 'lifecycle', 'lastMrr', 'users', 'ended'],
      filters: [
        { field: 'lifecycle', op: 'in', value: ['Deactivated', 'Disabled'] },
        STRIPE_BILLING,
      ],
      sort: { field: 'lastMrr', dir: 'desc' },
    },
    {
      id: 'enterprise',
      name: 'Enterprise contracts',
      description: 'Enterprise subscriptions with MRR today, largest first',
      columns: ['name', 'customerId', 'lifecycle', 'mrr', 'users', 'apps', 'nextDate'],
      filters: [{ field: 'offer', op: 'in', value: ['Enterprise'] }, PAYING],
      sort: { field: 'mrr', dir: 'desc' },
    },
    {
      id: 'customized',
      name: 'Customized plans',
      description: 'Paying subscriptions (MRR today) with additional user seats',
      columns: ['name', 'offer', 'billing', 'addons', 'mrr', 'nextDate'],
      filters: [{ field: 'hasAddons', op: 'isTrue' }, PAYING],
      sort: { field: 'mrr', dir: 'desc' },
    },
    {
      id: 'user-limit',
      name: 'At user limit',
      description:
        'Paying subscriptions whose users, open invitations included, reach the user limit. Upsell user seats.',
      columns: ['name', 'billing', 'addons', 'users', 'userLimit', 'mrr', 'nextDate'],
      filters: [{ field: 'atUserLimit', op: 'isTrue' }, PAYING],
      sort: { field: 'users', dir: 'desc' },
    },
    {
      id: 'unbilled',
      name: 'Licenses without Stripe',
      description:
        'Open as App licenses no Stripe subscription pays for: bulk grants and manual contracts',
      columns: ['name', 'offer', 'licensePlan', 'lifecycle', 'users', 'apps', 'started', 'ended'],
      filters: [{ field: 'billing', op: 'in', value: ['Unbilled'] }],
      sort: { field: 'started', dir: 'desc' },
    },
    {
      id: 'all-trials',
      name: 'All trials',
      description:
        'Every trial license, running or ended, also the ones listed as the Stripe subscription they are paired with',
      columns: ['name', 'offer', 'billing', 'lifecycle', 'users', 'apps', 'started', 'ended'],
      filters: [{ field: 'trialLicense', op: 'isTrue' }],
      sort: { field: 'started', dir: 'desc' },
    },
  ],
  users: [
    {
      id: 'all',
      name: 'All users',
      description: 'Every member of every Open as App team (api.TeamMembers)',
      columns: ['name', 'subscription', 'role', 'status', 'lastActive', 'created'],
      filters: [],
      sort: { field: 'lastActive', dir: 'desc' },
    },
    {
      id: 'on-paying',
      name: 'On paying subscriptions',
      description: 'Members of teams with an active or grace Stripe subscription',
      columns: ['name', 'subscription', 'offer', 'role', 'status', 'lastActive'],
      filters: [...ON_PAYING],
      sort: { field: 'lastActive', dir: 'desc' },
    },
    {
      id: 'no-subscription',
      name: 'Without a subscription',
      description: 'Members of teams that never had a license or Stripe subscription',
      columns: ['name', 'email', 'subscription', 'role', 'status', 'created'],
      filters: [{ field: 'subscriptionLifecycle', op: 'in', value: ['None'] }],
      sort: { field: 'created', dir: 'desc' },
    },
    {
      id: 'active',
      name: 'Active · 30 days',
      description:
        'Users of paying subscriptions who used Open as App in the 30 days up to the product data date',
      columns: ['name', 'subscription', 'offer', 'role', 'lastActive'],
      filters: [{ field: 'status', op: 'in', value: ['Active'] }, ...ON_PAYING],
      sort: { field: 'lastActive', dir: 'desc' },
    },
    {
      id: 'inactive-paying',
      name: 'Inactive on paying plans',
      description: 'No activity for 30+ days although the subscription is paid',
      columns: ['name', 'subscription', 'offer', 'role', 'lastActive', 'created'],
      filters: [{ field: 'status', op: 'in', value: ['Inactive'] }, ...ON_PAYING],
      sort: { field: 'lastActive', dir: 'asc' },
    },
    {
      id: 'admins',
      name: 'Owners and admins',
      description: 'Owners and admins of paying subscriptions: they manage users and billing',
      columns: ['name', 'email', 'subscription', 'role', 'lastActive'],
      filters: [{ field: 'role', op: 'in', value: ['Owner', 'Admin'] }, ...ON_PAYING],
      sort: { field: 'subscription', dir: 'asc' },
    },
    {
      id: 'invited',
      name: 'Invited, not joined',
      description: 'Open invitations to teams of paying subscriptions, oldest first',
      columns: ['name', 'email', 'subscription', 'role', 'created'],
      filters: [{ field: 'status', op: 'in', value: ['Invited'] }, ...ON_PAYING],
      sort: { field: 'created', dir: 'asc' },
    },
    {
      id: 'trial-teams',
      name: 'On trials',
      description: 'Members of teams running a trial license',
      columns: ['name', 'subscription', 'role', 'status', 'created'],
      filters: [
        { field: 'subscriptionBilling', op: 'in', value: ['Trial'] },
        { field: 'subscriptionLifecycle', op: 'in', value: ['Active'] },
      ],
      sort: { field: 'created', dir: 'desc' },
    },
    {
      id: 'new',
      name: 'Added · 30 days',
      description: 'Users of paying subscriptions added in the 30 days up to the product data date',
      columns: ['name', 'subscription', 'offer', 'role', 'status', 'created'],
      filters: [{ field: 'created', op: 'inLastDays', value: 30 }, ...ON_PAYING],
      sort: { field: 'created', dir: 'desc' },
    },
  ],
  apps: [
    {
      id: 'all',
      name: 'All apps',
      description: 'Every Open as App app (api.Apps), published or in draft',
      columns: [
        'name',
        'subscription',
        'type',
        'status',
        'dataSource',
        'visibility',
        'opens30d',
        'lastUsed',
        'updated',
      ],
      filters: [],
      sort: { field: 'updated', dir: 'desc' },
    },
    {
      id: 'on-paying',
      name: 'On paying subscriptions',
      description: 'Apps of teams with an active or grace Stripe subscription',
      columns: ['name', 'subscription', 'offer', 'type', 'status', 'updated'],
      filters: [...ON_PAYING],
      sort: { field: 'updated', dir: 'desc' },
    },
    {
      id: 'published',
      name: 'Published',
      description: 'Live apps, most recently updated first',
      columns: ['name', 'subscription', 'type', 'visibility', 'opens30d', 'lastUsed', 'updated'],
      filters: [{ field: 'status', op: 'in', value: ['Published'] }],
      sort: { field: 'updated', dir: 'desc' },
    },
    {
      id: 'used',
      name: 'Used in the last 30 days',
      description: 'Apps with app events in the last 30 days, most opened first',
      columns: [
        'name',
        'subscription',
        'status',
        'opens30d',
        'users30d',
        'activeDays30d',
        'platforms30d',
        'lastUsed',
      ],
      filters: [{ field: 'activeDays30d', op: 'gte', value: 1 }],
      sort: { field: 'opens30d', dir: 'desc' },
    },
    {
      id: 'unused',
      name: 'Published, not used in 30 days',
      description:
        'Published apps without app events in the last 30 days, most on deleted teams (iOS events are not loaded)',
      columns: ['name', 'subscription', 'type', 'visibility', 'lastUsed', 'updated'],
      filters: [
        { field: 'status', op: 'in', value: ['Published'] },
        { field: 'activeDays30d', op: 'lte', value: 0 },
      ],
      sort: { field: 'lastUsed', dir: 'desc' },
    },
    {
      id: 'unused-live',
      name: 'Not used · live subscriptions',
      description:
        'Published apps without app events in 30 days whose team has a running license or Stripe subscription: outreach candidates',
      columns: ['name', 'subscription', 'subscriptionLifecycle', 'type', 'lastUsed', 'updated'],
      filters: [
        { field: 'status', op: 'in', value: ['Published'] },
        { field: 'activeDays30d', op: 'lte', value: 0 },
        { field: 'subscriptionLifecycle', op: 'in', value: LIVE },
      ],
      sort: { field: 'lastUsed', dir: 'desc' },
    },
    {
      id: 'drafts',
      name: 'Drafts',
      description: 'Apps not yet published',
      columns: ['name', 'subscription', 'type', 'dataSource', 'created', 'updated'],
      filters: [{ field: 'status', op: 'in', value: ['Draft'] }],
      sort: { field: 'updated', dir: 'desc' },
    },
    {
      id: 'public',
      name: 'Public apps',
      description: 'Published apps reachable without sign-in',
      columns: ['name', 'subscription', 'offer', 'creator', 'opens30d', 'users30d', 'updated'],
      filters: [
        { field: 'visibility', op: 'in', value: ['Public'] },
        { field: 'status', op: 'in', value: ['Published'] },
      ],
      sort: { field: 'updated', dir: 'desc' },
    },
    {
      id: 'connected',
      name: 'Databases and APIs',
      description: 'Apps connected to SQL databases or REST services',
      columns: ['name', 'subscription', 'dataSource', 'status', 'updated'],
      filters: [
        { field: 'dataSource', op: 'in', value: ['MySQL', 'MS SQL', 'Postgres', 'REST API'] },
      ],
      sort: { field: 'updated', dir: 'desc' },
    },
    {
      id: 'stale',
      name: 'Not updated in 90 days',
      description: 'Published apps nobody has edited for three months, or never',
      columns: ['name', 'subscription', 'type', 'creator', 'updated'],
      filters: [
        { field: 'status', op: 'in', value: ['Published'] },
        { field: 'updated', op: 'notInLastDays', value: 90 },
      ],
      sort: { field: 'updated', dir: 'asc' },
    },
    {
      id: 'hidden',
      name: 'Hidden apps',
      description: 'Apps hidden from the app list of their team',
      columns: ['name', 'subscription', 'type', 'status', 'updated'],
      filters: [{ field: 'hidden', op: 'isTrue' }],
      sort: { field: 'updated', dir: 'desc' },
    },
    {
      id: 'new',
      name: 'Created · 30 days',
      description: 'Apps created in the 30 days up to the product data date, drafts included',
      columns: ['name', 'subscription', 'offer', 'type', 'status', 'created'],
      filters: [{ field: 'created', op: 'inLastDays', value: 30 }],
      sort: { field: 'created', dir: 'desc' },
    },
  ],
  /** People and teams that have never paid but show buying intent (growth snapshot). */
  leads: [
    {
      id: 'all',
      name: 'All leads',
      description: 'Highest intent first: abandoned checkouts, trials and recent sign-ups',
      columns: ['name', 'stage', 'leadType', 'score', 'signals', 'lastCheckout', 'trialEnds'],
      filters: [],
      sort: { field: 'score', dir: 'desc' },
    },
    {
      id: 'abandoned',
      name: 'Abandoned checkouts',
      description: 'Opened Stripe Checkout in the last 400 days without buying',
      columns: ['name', 'leadType', 'checkoutValue', 'checkoutSessions', 'apps', 'lastCheckout'],
      filters: [{ field: 'stage', op: 'in', value: ['Abandoned checkout'] }],
      sort: { field: 'lastCheckout', dir: 'desc' },
    },
    {
      id: 'checkout-14',
      name: 'Checkout · last 14 days',
      description: 'Still deciding: follow up while the purchase is on their mind',
      columns: ['name', 'leadType', 'checkoutValue', 'checkoutSessions', 'signals', 'lastCheckout'],
      filters: [{ field: 'lastCheckout', op: 'inLastDays', value: 14 }],
      sort: { field: 'lastCheckout', dir: 'desc' },
    },
    {
      id: 'trials',
      name: 'Trials running',
      description: 'Running trial licenses, earliest end first (as of the product data date)',
      columns: ['name', 'leadType', 'apps', 'publishedApps', 'members', 'lastActive', 'trialEnds'],
      filters: [{ field: 'stage', op: 'in', value: ['Trial running'] }],
      sort: { field: 'trialEnds', dir: 'asc' },
    },
    {
      id: 'trials-ended',
      name: 'Trials ended',
      description: 'Trials that ended in the 60 days before the product data date, not converted',
      columns: ['name', 'leadType', 'apps', 'publishedApps', 'members', 'trialEnds'],
      filters: [{ field: 'stage', op: 'in', value: ['Trial ended'] }],
      sort: { field: 'score', dir: 'desc' },
    },
    {
      id: 'business',
      name: 'Business leads',
      description: 'Leads with a company email domain, at any stage',
      columns: ['name', 'domain', 'stage', 'apps', 'publishedApps', 'signedUp', 'score'],
      filters: [{ field: 'leadType', op: 'in', value: ['Business'] }],
      sort: { field: 'score', dir: 'desc' },
    },
    {
      id: 'business-published',
      name: 'Business, published an app',
      description: 'Company domain and a published app, not paying yet',
      columns: ['name', 'domain', 'stage', 'apps', 'publishedApps', 'members', 'score'],
      filters: [
        { field: 'leadType', op: 'in', value: ['Business'] },
        { field: 'publishedApps', op: 'gte', value: 1 },
      ],
      sort: { field: 'score', dir: 'desc' },
    },
    {
      id: 'teams',
      name: 'Teams inviting colleagues',
      description: 'Three or more team members, not paying yet',
      columns: ['name', 'leadType', 'stage', 'members', 'apps', 'score'],
      filters: [{ field: 'members', op: 'gte', value: 3 }],
      sort: { field: 'members', dir: 'desc' },
    },
  ],
};
